import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunityPost } from '~/interfaces/community';
import { useShortRelativeDate } from '~/routes/community/_hooks/useShortRelativeDate';

interface PostResultRowProps {
  post: CommunityPost;
  onClick?: () => void;
}

export const PostResultRow: FC<PostResultRowProps> = ({ post, onClick }) => {
  const formatRelativeTime = useShortRelativeDate();
  const { t } = useTranslation();

  const original = post.attachable?.type === 'quoted_post' ? post.attachable : undefined;
  const isBareRepost = Boolean(original) && !post.title?.trim() && !post.body_preview;

  const title =
    post.title?.trim() || original?.title?.trim() || (original ? t('community.repost.repost', 'Repost') : post.title);

  const subtitle = isBareRepost
    ? t('community.repost.reposted_by', 'Reposted by {{name}}', {
        name: post.author.name || t('community.join.former_member', 'Former member'),
        interpolation: { escapeValue: false },
      })
    : post.body_preview;

  return (
    <Link
      to={`${COMMUNITY_ROUTES.root}/posts/${post.id}`}
      onClick={onClick}
      className="flex items-center gap-2 h-[72px] py-4 pr-4 pl-2 hover:bg-black/5 transition-colors"
    >
      <div className="flex items-center justify-center size-8 shrink-0">
        <DocumentTextIcon className="size-4 text-black/50" />
      </div>
      <div className="flex flex-col justify-center gap-1 min-w-0 flex-1 pr-4">
        <span className="truncate text-[15px] leading-5 font-semibold tracking-[-0.01em] text-black">{title}</span>
        {subtitle && (
          <span className="truncate text-[13px] leading-4 tracking-[-0.01em] text-black/70">{subtitle}</span>
        )}
      </div>
      <span className="shrink-0 text-[13px] leading-4 tracking-[-0.01em] text-black/50">
        {formatRelativeTime(post.created_at)}
      </span>
    </Link>
  );
};
